import { icon } from '@carvana/ds-styles';
import { Clickable } from '@carvana/merch-components';
import {
  VehicleOffering,
  type InventoryVehicle
} from '@carvana/merch-contracts';
import { useDebouncedCallback } from '@carvana/merch-react-hooks';
import cx from 'classnames';
import { Heart } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import TagId from '~/constants/common/TagId';
import { VdpCardType } from '~/constants/home/VdpCardType';
import { useTrackEvent } from '~/context/common/AnalyticsProvider';
import { useFavoritesIds, useIsLoggedIn } from '~/context/common/UserContext';
import { useToggleFavorite } from '~/hooks/common/favorites';
import { getNativeVdpPayload } from '~/native/common/utils/getNativePayloads';
import getImageToUse from './getImageToUse';
import getVehicleStatus from './getVehicleStatus';
import Price from './Price';
import StatusTag from './StatusTag';
import {
  InformationContainer,
  ThumbnailContainer,
  VehicleLinkCardContainer,
  VehicleLinkCardWrapper,
  VehicleTrim,
  VehicleYearMakeModel
} from './VehicleLinkCard.styles';

type OwnProps = {
  type: VdpCardType;
  vehicle: InventoryVehicle;
};

export default function VehicleLinkCard({ type, vehicle }: OwnProps) {
  const {
    year,
    make,
    model,
    offering,
    jellyBeanMobileUrl,
    trim,
    price,
    vehicleId,
    stockNumber,
    vehicleUnlockDateTime,
    previousPrice,
    vehicleTags = []
  } = vehicle;
  const { total = 0, msrp = 0 } = price ?? {};
  const isNewVehicle = offering === VehicleOffering.New;
  const hasPriceDrop = vehicleTags.some(tag => tag.tagId === TagId.PriceDrop);
  const [imgError, setImgError] = useState(false);
  const trackEvent = useTrackEvent();
  const isLoggedIn = useIsLoggedIn();
  const status = getVehicleStatus({ ...vehicle });
  const toggleFavorite = useToggleFavorite();
  const favoriteIds = useFavoritesIds();
  const [isFavoritedLocally, setFavoritedLocally] = useState(
    favoriteIds.includes(vehicleId)
  );

  const handleFavorite = useDebouncedCallback<
    React.MouseEventHandler<HTMLElement>
  >(
    () => {
      toggleFavorite({ vehicleId, stockNumber });
      if (isLoggedIn) setFavoritedLocally(v => !v);
      trackEvent('Home Feed Card', {
        action: 'clicked',
        actionTaken: isFavoritedLocally ? 'removed' : 'added',
        description: 'VDP card',
        type,
        item: 'favorite heart',
        vehicleId
      });
    },
    [
      isFavoritedLocally,
      isLoggedIn,
      stockNumber,
      toggleFavorite,
      trackEvent,
      type,
      vehicleId
    ],
    500,
    {
      leading: true,
      onEveryCall: ev => {
        ev.preventDefault();
        ev.stopPropagation();
      },
      trailing: false
    }
  );

  useEffect(() => {
    setFavoritedLocally(favoriteIds.includes(vehicle.vehicleId));
  }, [favoriteIds, vehicle.vehicleId]);

  return (
    <VehicleLinkCardWrapper>
      <VehicleLinkCardContainer
        data-analytics-id="Home feed card"
        data-analytics-attributes={JSON.stringify({
          description: 'VDP card',
          type,
          item: 'vehicle'
        })}
        data-native-payload={getNativeVdpPayload(vehicleId)}
        data-testid="vdp-link-vehicle"
        href={`/vehicle/${vehicleId}?refSource=home`}
      >
        <InformationContainer>
          <StatusTag
            status={status}
            vehicleUnlockDateTime={vehicleUnlockDateTime}
          />
          <div className="flex flex-col">
            <VehicleYearMakeModel>
              {year} {make} {model}
            </VehicleYearMakeModel>
            <VehicleTrim>{trim}</VehicleTrim>
          </div>
          <Price
            hasPriceDrop={hasPriceDrop}
            isNewVehicle={isNewVehicle}
            msrp={msrp}
            previousPrice={previousPrice}
            total={total}
          />
        </InformationContainer>
        <ThumbnailContainer>
          <img
            alt=""
            onError={() => setImgError(true)}
            className="block max-h-[103px] w-full overflow-hidden rounded-md bg-background-weak object-cover"
            src={imgError ? jellyBeanMobileUrl : getImageToUse({ ...vehicle })}
            data-testid="vdp-link-vehicle-image"
          />
        </ThumbnailContainer>
      </VehicleLinkCardContainer>
      <Clickable
        aria-label={
          isFavoritedLocally ? 'Remove from favorites' : 'Add to favorites'
        }
        aria-pressed={isFavoritedLocally}
        className="absolute right-4 top-4"
        data-analytics-id={null}
        data-testid="vdp-link-favorite-button"
        onClick={handleFavorite}
      >
        <Heart
          aria-hidden="true"
          color={isFavoritedLocally ? icon.critical : icon.strong}
          className={cx(
            'hover:!stroke-icon-critical',
            isFavoritedLocally ? 'fill-icon-critical' : 'fill-icon-inverse'
          )}
        />
      </Clickable>
    </VehicleLinkCardWrapper>
  );
}
